import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { fromEvent, Observable } from 'rxjs';
import { ThemeService } from './services/theme/theme.service';
import { OverlayContainer } from '@angular/cdk/overlay';
import { STORAGE_KEYS } from './services/storage/storage.service';
import { delay, filter, takeUntil } from 'rxjs/operators';
import { CubeService } from './services';
import { UnsubscriptionHandler } from './abstractions/unsubscription-handler.class';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { Store } from '@ngrx/store';
import { AppState, TokenDetails } from './store/states/auth.state';
import { SystemNotification } from './components/core/notification-snackbar/models/system-notification.class';
import { NotificationSnackbarComponent } from './components/core/notification-snackbar/notification-snackbar.component';
import {
  selectLoading,
  selectNotifications,
} from './store/selectors/system.selectors';
import { AuthActions } from './store/action-types/auth.action-types';
import jwtDecode from 'jwt-decode';
import { AuthService } from './services/auth/auth.service';
import { TokenService } from './services/auth/token.service';
import { selectTokenDetails, selectUserTenants } from './store/selectors/auth.selectors';
import { concatLatestFrom } from '@ngrx/effects';

@Component({
  selector: 'cube-app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent extends UnsubscriptionHandler implements OnInit {
  isDarkTheme: boolean;
  loading$: Observable<boolean>;
  constructor(
    translate: TranslateService,
    private overlayContainer: OverlayContainer,
    private themeService: ThemeService,
    private cubeService: CubeService,
    private readonly store: Store<AppState>,
    private readonly snackbar: MatSnackBar,
    private readonly authService: AuthService,
    private readonly tokenService: TokenService
  ) {
    super();

    // this language will be used as a fallback when a translation isn't found in the current language
    translate.setDefaultLang('en');
    // a cosa serve?
    // this.title.setTitle(environment.SOLUTION === Solutions.FLEET40 ? 'RINA FLEET' : 'CUBE - OPTIMUM');

    // the lang to use, if the lang isn't available, it will use the current loader to get them
    localStorage.setItem(
      STORAGE_KEYS.localTimeFormat,
      translate.getBrowserLang()
    );
    const browserLang = translate.getBrowserLang();
    translate.use(browserLang.match(/en|it/) ? browserLang : 'en');

    this.loading$ = this.store.select(selectLoading).pipe(delay(500));
    this.store.dispatch(AuthActions.Login());
    this.authService.token
      .pipe(filter((token: string | undefined): token is string => !!token))
      .subscribe((token) => {
        const uniqueId = this.tokenService.getUniqueId(token);
        this.store.dispatch(
          AuthActions.SetTokenDetails({
            tokenDetails: { ...jwtDecode(token), uniqueId } as TokenDetails,
          })
        );
      });

      this.store
      .select(selectTokenDetails)
      .pipe(
        filter(
          (
            tokenDetails: TokenDetails | undefined
          ): tokenDetails is TokenDetails => !!tokenDetails
        ),
        concatLatestFrom(() => this.store.select(selectUserTenants)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: ([tokenDetails, tenants]) => {
          if (!tenants.length) {
            this.store.dispatch(
              AuthActions.GetUserTenants({ uniqueId: tokenDetails.uniqueId })
            );
          }
        },
      });
  }

  getRouteAnimation(outlet): void {
    return outlet.activatedRouteData.animation;
  }

  ngOnInit(): void {
    this.themeService.isDarkTheme
      .pipe(takeUntil(this.destroy$))
      .subscribe((isDark) => {
        this.isDarkTheme = isDark;
        this.overlayContainer
          .getContainerElement()
          .classList.remove(isDark ? 'light-theme' : 'dark-theme');
        this.overlayContainer
          .getContainerElement()
          .classList.add(!isDark ? 'light-theme' : 'dark-theme');
      });

    fromEvent(window, 'storage')
      .pipe(
        filter((ev: StorageEvent) => ev.key === STORAGE_KEYS.currentTenant),
        takeUntil(this.destroy$)
      )
      .subscribe((storageEvent: StorageEvent) => {
        this.cubeService.cleanCache();
        window.location.reload();
      });

    this.store
      .select(selectNotifications)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (notification: SystemNotification | undefined): void => {
          if (notification) {
            const {
              duration,
              horizontalPosition,
              verticalPosition,
              panelClass,
              data,
            } = notification.options;
            const snackBarConfig: MatSnackBarConfig = {
              duration,
              horizontalPosition,
              verticalPosition,
              panelClass,
              data,
            };

            this.snackbar.openFromComponent(
              NotificationSnackbarComponent,
              snackBarConfig
            );
          }
        },
      });
  }
}
